import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  ClockClockwise, ChatCircleDots, UsersThree, Star,
  CaretDown, CaretUp, WhatsappLogo, InstagramLogo, EnvelopeSimple,
  Globe, ChatCircle, CircleNotch,
} from "@phosphor-icons/react";

const CH_ICON = {
  web: Globe, whatsapp: WhatsappLogo, instagram: InstagramLogo,
  messenger: ChatCircle, email: EnvelopeSimple,
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor((now - d) / 86400000);
  if (days === 0) return `Hoy · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  if (days === 1) return "Ayer";
  if (days < 7) return `Hace ${days}d`;
  return d.toLocaleDateString();
}

function StatusPill({ status }) {
  const map = {
    open: "bg-blue-500/15 text-blue-700 border-blue-500/30",
    closed: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
    escalated: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    pending: "bg-slate-500/15 text-slate-700 border-slate-500/30",
  };
  return (
    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

export default function CustomerHistoryPanel({ customerId, currentConversationId, onSelectConversation, lang }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId || !expanded) return;
    setData(null);
    setLoading(true);
    api.get(`/customers/${customerId}/history`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [customerId, expanded]);

  return (
    <div className="mt-6" data-testid="customer-history">
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="history-toggle"
        className="w-full flex items-center justify-between text-left mb-2 group"
      >
        <span className="overline flex items-center gap-1.5">
          <ClockClockwise size={11} /> {lang === "es" ? "Histórico del cliente" : "Customer history"}
        </span>
        {expanded ? <CaretUp size={12} /> : <CaretDown size={12} className="text-muted-foreground group-hover:text-foreground" />}
      </button>

      {!expanded && (
        <div className="text-[10px] text-muted-foreground">
          {lang === "es" ? "Toca para ver todas las interacciones previas de este cliente." : "Tap to see all previous interactions."}
        </div>
      )}

      {expanded && loading && (
        <div className="text-xs text-muted-foreground flex items-center gap-1.5 py-2">
          <CircleNotch size={11} className="animate-spin" /> {lang === "es" ? "Cargando…" : "Loading…"}
        </div>
      )}

      {expanded && data && !loading && (
        <div className="space-y-3">
          {/* Stats mini */}
          <div className="grid grid-cols-3 gap-1.5" data-testid="history-stats">
            <div className="rounded-md border border-border p-2 text-center">
              <div className="text-lg font-heading font-bold leading-none">{data.stats.conversations_count}</div>
              <div className="text-[9px] uppercase text-muted-foreground mt-1">
                {lang === "es" ? "Convs" : "Convos"}
              </div>
            </div>
            <div className="rounded-md border border-border p-2 text-center">
              <div className="text-lg font-heading font-bold leading-none">{data.stats.leads_count}</div>
              <div className="text-[9px] uppercase text-muted-foreground mt-1">Leads</div>
            </div>
            <div className="rounded-md border border-border p-2 text-center">
              <div className="text-lg font-heading font-bold leading-none">
                {data.stats.avg_csat ? `${data.stats.avg_csat}★` : "—"}
              </div>
              <div className="text-[9px] uppercase text-muted-foreground mt-1">CSAT</div>
            </div>
          </div>

          {data.stats.first_contact && (
            <div className="text-[10px] text-muted-foreground">
              {lang === "es" ? "Primer contacto: " : "First contact: "} {fmtDate(data.stats.first_contact)}
              {" · "}
              {data.stats.channels_used.length} {lang === "es" ? "canal(es)" : "channel(s)"}
            </div>
          )}

          {/* Conversations timeline */}
          <div>
            <div className="text-[10px] uppercase text-muted-foreground mb-1.5 font-semibold">
              {lang === "es" ? "Conversaciones" : "Conversations"}
            </div>
            {data.conversations.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic">
                {lang === "es" ? "Sin conversaciones previas." : "No previous conversations."}
              </div>
            ) : (
              <div className="space-y-1.5 max-h-60 overflow-auto pr-1" data-testid="history-conversations">
                {data.conversations.map((c) => {
                  const isCurrent = c.id === currentConversationId;
                  const ChIcon = CH_ICON[c.channel] || Globe;
                  return (
                    <button
                      key={c.id}
                      onClick={() => !isCurrent && onSelectConversation?.(c.id)}
                      disabled={isCurrent}
                      data-testid={`history-conv-${c.id}`}
                      className={`w-full text-left rounded-md border p-2 transition-all ${
                        isCurrent
                          ? "border-primary bg-primary/5 ring-1 ring-primary/20 cursor-default"
                          : "border-border hover:border-primary/40 hover:bg-muted/40 cursor-pointer"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <ChIcon size={10} className="text-muted-foreground shrink-0" />
                        <span className="text-[10px] text-muted-foreground font-mono">{fmtDate(c.created_at)}</span>
                        <span className="ml-auto flex items-center gap-1">
                          {isCurrent && <Badge variant="outline" className="text-[8px] uppercase h-4">
                            {lang === "es" ? "Actual" : "Current"}
                          </Badge>}
                          <StatusPill status={c.status} />
                        </span>
                      </div>
                      {c.last_message_preview && (
                        <div className="text-[11px] line-clamp-2 leading-tight">
                          {c.last_message_preview}
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-2 text-[9px] text-muted-foreground">
                        <span>{c.message_count} msgs</span>
                        {c.agent_name && <span>· {c.agent_name}</span>}
                        {c.business_unit && <span>· {c.business_unit}</span>}
                        {c.csat_score && <span className="flex items-center gap-0.5"><Star size={8} weight="fill" className="text-amber-500"/>{c.csat_score}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent leads (compact) */}
          {data.leads.length > 0 && (
            <div>
              <div className="text-[10px] uppercase text-muted-foreground mb-1.5 font-semibold flex items-center gap-1">
                <UsersThree size={10}/> Leads ({data.leads.length})
              </div>
              <div className="space-y-1 max-h-32 overflow-auto pr-1">
                {data.leads.slice(0, 5).map((l) => (
                  <div key={l.id} className="text-[11px] flex items-center justify-between border-b border-border/40 py-1" data-testid={`history-lead-${l.id}`}>
                    <span className="truncate">{l.brand || "—"} {l.model || ""}</span>
                    <span className="flex items-center gap-1 shrink-0">
                      <Badge variant="outline" className="text-[8px] uppercase h-4">{l.intent}</Badge>
                      <span className="text-[9px] font-mono text-muted-foreground">{fmtDate(l.created_at)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
